import { memo, useCallback, useMemo } from 'react';
import { getAllCompositeSegments, isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { CLIP_CUSTOMIZER_LAYOUT_TYPES } from '@goldcast/player-config/src/constants/layouts.constants';
import { LayoutType } from '@goldcast/player-config/src/types/asset-types';
import ClipCustomizerMagicLayoutIconButton from './ClipCustomizerMagicLayoutIconButton';
import { LayoutPopupOption } from '../../ClipCustomizer/Layout/constants';
import { CLIP_UPDATE_TYPE } from '../../ClipNewTimeline/clipUpdateDataUtils/constants';
import { updateClipData } from '../../ClipNewTimeline/clipUpdateDataUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useDialog from '@/components/organisms/useDialog';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

function ClipCustomizerMagicLayoutOption({
  magicLayoutOption,
  disabled,
  disabledReason
}: {
  magicLayoutOption: LayoutType;
  disabled: boolean;
  disabledReason: string;
}) {
  const { clipId, clipData } = useClipsContext();
  const { isOpen, openDialog, closeDialog } = useDialog();
  const splitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clipData,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });

  const clipLayout = useMemo(() => {
    if (splitsEnabled) {
      const allScenesLayouts = getAllCompositeSegments(clipData).flatMap(compositeSegment =>
        compositeSegment.scenes.map(scene => scene.details.layout)
      );
      const layouts = new Set(allScenesLayouts);
      if (layouts.size > 1) {
        return clipData.asset_metadata.layout;
      }
      return allScenesLayouts[0];
    }
    const scenes = clipData.asset_metadata.scenes;
    if (scenes && scenes.length === 1) {
      return scenes[0].details.layout;
    }
    return clipData.asset_metadata.layout;
  }, [clipData, splitsEnabled]);

  const segmentHasMultipleScenes = useMemo(() => {
    if (splitsEnabled) {
      return getAllCompositeSegments(clipData).find(compositeSegment => compositeSegment.scenes.length > 1);
    }
    return false;
  }, [clipData, splitsEnabled]);

  const hasMultipleScenes = useMemo(() => {
    if (splitsEnabled) {
      const allScenesLayouts = getAllCompositeSegments(clipData).flatMap(compositeSegment =>
        compositeSegment.scenes.map(scene => scene.details.layout)
      );
      const layouts = new Set(allScenesLayouts);
      return layouts.size > 1;
    }
    return clipData.asset_metadata.scenes && clipData.asset_metadata.scenes.length > 1;
  }, [clipData, splitsEnabled]);

  const handleLayoutChange = useCallback(() => {
    if (hasMultipleScenes || segmentHasMultipleScenes) {
      openDialog();
      return;
    }

    if (clipLayout === magicLayoutOption) {
      return;
    }

    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
      layout: magicLayoutOption
    });
  }, [clipId, clipLayout, magicLayoutOption, hasMultipleScenes, segmentHasMultipleScenes, openDialog]);

  const handleConfirm = useCallback(() => {
    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
      layout: magicLayoutOption
    });
    closeDialog();
  }, [clipId, magicLayoutOption, closeDialog]);

  const handleCancel = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  const optionDetails = CLIP_CUSTOMIZER_LAYOUT_TYPES.find(
    option => option.value === magicLayoutOption
  ) as LayoutPopupOption;

  const isSingleSceneSplits = splitsEnabled && !segmentHasMultipleScenes;

  return (
    <>
      <ClipCustomizerMagicLayoutIconButton
        optionDetails={optionDetails}
        trackingId={`customizer-magic-layout-${magicLayoutOption}`}
        onClick={handleLayoutChange}
        isDisabled={disabled}
        disabledReason={disabledReason}
        isActive={!hasMultipleScenes && clipLayout === magicLayoutOption}
        tooltipPosition={'top'}
      />
      <ConfirmationDialog
        isOpen={isOpen}
        title="Confirm Layout Change"
        content={
          isSingleSceneSplits || magicLayoutOption !== 'AUDIOGRAM'
            ? `This will update all the existing scenes to use the selected layout. Do you want to continue?`
            : `This will delete all existing scenes in this segment and replace them with a single layout. Are you sure you want to continue?`
        }
        confirmLabel="Continue"
        cancelLabel="Cancel"
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
}

export default memo(ClipCustomizerMagicLayoutOption);
